import type { HistoryItem } from "@/services/historyService";

export interface SkillProgress {
  currentNclc: number;
  targetNclc: number;
  currentScore: number;
  targetScore: number;
  status: string;
  progressPercent: number;
  hasData: boolean;
}

export function parseScoreValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Caso "233 / 699"
    const firstPart = trimmed.split("/")[0]?.trim();
    const numeric = Number(firstPart.replace(",", "."));

    return Number.isFinite(numeric) ? numeric : null;
  }

  return null;
}

export function parseNclcLevel(value: unknown): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const match = value.match(/\d+/);
    if (!match) return null;

    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

export function parseNclcNumber(nclcStr?: string | null): number {
  return parseNclcLevel(nclcStr) ?? 0;
}

export function getBestSkillResult(history: HistoryItem[], skill: "ce" | "co" | "ee" | "eo"): { nclc: number; score: number } | null {
  if (!history || history.length === 0) return null;

  let bestNclc = 0;
  let bestScore = 0;
  let found = false;

  for (const item of history) {
    if (!item) continue;
    const itemAny = item as any;
    const comp = (item.competence || "").toLowerCase();
    const type = item.test_type || item.testType || item.mode;

    let match = false;
    let nclcVal: number | null = null;
    let scoreVal: number | null = null;

    if (skill === "ce") {
      if (comp.includes("reading") || comp.includes("ce") || comp.includes("comprehension ecrite") || comp.includes("compréhension écrite")) {
        match = true;
      } else if (type === "complete" || type === "full_exam") {
        if (itemAny.result?.ce || itemAny.result?.reading || itemAny.sections?.reading) {
          match = true;
        }
      }

      if (match) {
        found = true;
        nclcVal = parseNclcLevel(itemAny.nclcEstimate) ??
                  parseNclcLevel(itemAny.result?.ce?.nclc) ??
                  parseNclcLevel(itemAny.result?.reading?.nclc) ??
                  parseNclcLevel(itemAny.sections?.reading?.nclc);

        scoreVal = parseScoreValue(itemAny.estimatedScore) ??
                   parseScoreValue(itemAny.result?.reading?.score) ??
                   parseScoreValue(itemAny.result?.ce?.score) ??
                   parseScoreValue(itemAny.sections?.reading?.score) ??
                   parseScoreValue(itemAny.score);
      }
    } else if (skill === "co") {
      if (comp.includes("listening") || comp.includes("co") || comp.includes("comprehension orale") || comp.includes("compréhension orale")) {
        match = true;
      } else if (type === "complete" || type === "full_exam") {
        if (itemAny.result?.co || itemAny.result?.listening || itemAny.sections?.listening) {
          match = true;
        }
      }

      if (match) {
        found = true;
        nclcVal = parseNclcLevel(itemAny.nclcEstimate) ??
                  parseNclcLevel(itemAny.result?.co?.nclc) ??
                  parseNclcLevel(itemAny.result?.listening?.nclc) ??
                  parseNclcLevel(itemAny.sections?.listening?.nclc);

        scoreVal = parseScoreValue(itemAny.estimatedScore) ??
                   parseScoreValue(itemAny.result?.listening?.score) ??
                   parseScoreValue(itemAny.result?.co?.score) ??
                   parseScoreValue(itemAny.sections?.listening?.score) ??
                   parseScoreValue(itemAny.score);
      }
    } else if (skill === "ee") {
      if (comp.includes("writing") || comp.includes("ee") || comp.includes("expression ecrite") || comp.includes("expression écrite") || itemAny.isWritingCorrection) {
        match = true;
      }

      if (match) {
        found = true;
        nclcVal = parseNclcLevel(itemAny.nclcEstimate) ??
                  parseNclcLevel(itemAny.aiCorrection?.nclc) ??
                  parseNclcLevel(itemAny.writingCorrection?.nclc);

        scoreVal = parseScoreValue(itemAny.estimatedScore) ??
                   parseScoreValue(itemAny.result?.writing?.score) ??
                   parseScoreValue(itemAny.result?.ee?.score) ??
                   parseScoreValue(itemAny.aiCorrection?.score) ??
                   parseScoreValue(itemAny.writingCorrection?.score) ??
                   parseScoreValue(itemAny.score);
      }
    }

    if (nclcVal !== null && nclcVal > bestNclc) bestNclc = nclcVal;
    if (scoreVal !== null && scoreVal > bestScore) bestScore = scoreVal;
  }

  if (!found && bestNclc === 0 && bestScore === 0) return null;

  return { nclc: bestNclc || Math.min(10, Math.ceil(bestScore / 35)), score: bestScore };
}

export function getLatestSkillResult(history: HistoryItem[], skill: "ce" | "co" | "ee" | "eo"): { nclc: number; score: number } | null {
  return getBestSkillResult(history, skill);
}

export function calculateProgressToGoal(currentNclc: number, targetNclc: number, t?: any): string {
  if (!currentNclc || currentNclc === 0) {
    return t ? t("dashboard.noDataYet", "Sem dados ainda") : "Sem dados ainda";
  }
  if (currentNclc >= targetNclc) {
    return t ? t("dashboard.goalAchieved", "Meta atingida") : "Meta atingida";
  }
  const diff = targetNclc - currentNclc;
  if (t) {
    if (diff === 1) {
      return t("dashboard.nclcLevelsMissing_one", "Falta 1 nível NCLC");
    }
    return t("dashboard.nclcLevelsMissing_other", "Falta {{count}} níveis NCLC", { count: diff });
  }
  return `Falta ${diff} nível${diff > 1 ? "s" : ""} NCLC`;
}

export function calculateScoreGap(currentScore: number, targetScore: number): number {
  return Math.max(0, targetScore - currentScore);
}

export function getSkillStatus(currentNclc: number, targetNclc: number): "achieved" | "progress" | "nodata" {
  if (!currentNclc || currentNclc === 0) return "nodata";
  if (currentNclc >= targetNclc) return "achieved";
  return "progress";
}
